import { motion } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageContext";
import { PHILOSOPHY } from "@/lib/assets";

const reveal = {
  hidden: { opacity: 0, y: 50 },
  show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.22, 1, 0.36, 1] } },
};

const VP_100 = { once: true, margin: "-100px" };
const VP_80 = { once: true, margin: "-80px" };
const VP_60 = { once: true, margin: "-60px" };
const VP_ONCE = { once: true };

export const Transmission = () => {
  const { t } = useLanguage();
  const maxims = t("about.maxims");
  const goConsult = () =>
    document.querySelector("#consultation")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="transmission"
      data-testid="transmission-section"
      className="relative py-28 sm:py-40 bg-[#0B1622] overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={VP_100}
          className="max-w-3xl"
        >
          <p className="text-[#D4AF37] tracking-[0.3em] uppercase text-xs mb-8">
            {t("transmission.eyebrow")}
          </p>
          <h2 className="font-serif-lux text-4xl sm:text-5xl lg:text-6xl leading-tight text-white">
            {t("transmission.title")}
            <span className="gold-gradient-text italic">{t("transmission.titleAccent")}</span>
          </h2>
          <p className="mt-6 text-[#A0ABC0] text-base sm:text-lg font-light leading-relaxed">
            {t("transmission.intro")}
          </p>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={VP_80}
            className="lg:col-span-7 order-2 lg:order-1"
          >
            <div className="border-l-2 border-[#D4AF37]/40 pl-8 sm:pl-12">
              <blockquote
                data-testid="transmission-quote"
                className="font-serif-lux italic text-2xl sm:text-3xl lg:text-[2.4rem] leading-snug text-white"
              >
                {t("about.quote")}
              </blockquote>
              <p className="mt-6 text-[#D4AF37] text-xs sm:text-sm uppercase tracking-[0.28em]">
                {t("about.quoteAuthor")}
              </p>
            </div>
          </motion.div>

          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={VP_80}
            className="lg:col-span-5 order-1 lg:order-2"
          >
            <div className="relative overflow-hidden">
              <img
                src={PHILOSOPHY.pillars[0]}
                alt="La transmission — les fondations"
                className="w-full h-[420px] sm:h-[520px] object-cover"
              />
              <div className="absolute inset-0 border border-[#D4AF37]/25" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1622]/80 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6" data-testid="transmission-lux">
                <p className="gold-gradient-text font-serif-lux italic text-lg sm:text-2xl">
                  {t("philosophy.luxemburgensia")}
                </p>
                <p className="text-white/60 text-xs tracking-[0.15em] uppercase mt-1">
                  {t("philosophy.luxemburgensiaDesc")}
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        <div className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-10 sm:gap-8">
          {maxims.map((m, i) => (
            <motion.div
              key={m}
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={VP_60}
              transition={{ delay: i * 0.12 }}
              data-testid={`transmission-maxim-${i}`}
              className="border-t border-[#D4AF37]/25 pt-6"
            >
              <span className="font-serif-lux text-sm text-[#D4AF37]/70">0{i + 1}</span>
              <p className="mt-3 font-serif-lux italic text-lg sm:text-xl text-white leading-snug">
                {m}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          variants={reveal}
          initial="hidden"
          whileInView="show"
          viewport={VP_ONCE}
          className="mt-16"
        >
          <button
            onClick={goConsult}
            data-testid="transmission-cta"
            className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1420] transition-colors duration-300 px-10 py-4 uppercase tracking-widest text-xs"
          >
            {t("manifesto.cta")}
          </button>
        </motion.div>
      </div>
    </section>
  );
};
